/**
 * PURPOSE: Editor de UNA serie/final/concurso de Atletismo.
 * RESPONSIBILITIES: Mantener el borrador local de la serie, mostrar la grilla del formato, el viento
 *   por serie, el estado de la serie, el indicador de cambios y "Guardar" (también Ctrl+S), que
 *   envía un `RoundSave` PARCIAL con solo las filas cambiadas y `expectedRevision`.
 *
 *   EL SERVIDOR DECIDE. Tras guardar, el borrador se reconstruye con la serie que devolvió el
 *   servidor (lugares, mejores marcas, "q", `advancesToFinal`). Nada se ordena ni puntúa aquí.
 *
 *   ERRORES. 409 `REVISION_CONFLICT` sube al detalle (banner "Otro usuario guardó cambios" con
 *   Recargar). 400 `INVALID_MARK` resalta la celda que indica `details`. Las celdas con forma local
 *   inválida se pintan en rojo al salir de ellas y bloquean el guardado.
 *
 *   EDITAR LA SERIE. `headerActions` (el "+" de agregar) va junto al horario; `notice` explica un bloqueo (serie
 *   terminada). Una serie vacía de pista con carriles muestra sus carriles libres para llenarlos (`onAddEntry`,
 *   `onSearchAtLane`); las acciones de cada fila llegan en `rowActions(entry)`.
 * COLLABORATORS: PruebaDetailPanel.jsx, grids/*, utils/athleticsRoundDraft.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/RoundEditor.jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { FiClock, FiLock, FiRotateCcw, FiSave } from "react-icons/fi";

import { normalizeWind, sanitizeWindTyping } from "../../../utils/athleticsMarks.js";
import {
  buildRoundDraft,
  buildRoundSave,
  changedRowIds,
  collectInvalidCells,
  hasRoundSaveChanges,
  invalidCellKeys,
  isServerInvalidCell,
  resolveAttemptSlots,
  resolveAttemptsPerHeight,
  resolveInvalidMarkTarget,
} from "../../../utils/athleticsRoundDraft.js";
import { parseAthleticsError } from "../../../utils/athleticsResultsErrors.js";
import { ROUND_STATUS_OPTIONS, formatShortDate, formatTime } from "../labels.js";
import { DISCARD_CHANGES_OPTIONS, useConfirmDialog } from "./ConfirmDialog.jsx";
import {
  Badge,
  EmptyState,
  Spinner,
  primaryButtonClass,
  compactSelectClass,
  secondaryButtonClass,
} from "./ui.jsx";
import { CellInput, useGridNavigation } from "./grids/gridCells.jsx";
import TrackGrid from "./grids/TrackGrid.jsx";
import RelayGrid from "./grids/RelayGrid.jsx";
import FieldGrid from "./grids/FieldGrid.jsx";
import VerticalGrid from "./grids/VerticalGrid.jsx";

const GRID_BY_FORMAT = {
  CPT: TrackGrid,
  MAR: TrackGrid,
  PST: RelayGrid,
  SHL: FieldGrid,
  SVA: VerticalGrid,
};

const RoundSchedule = ({ schedule }) => {
  if (!schedule || (!schedule.date && !schedule.time)) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500">
      <FiClock aria-hidden="true" />
      {formatShortDate(schedule.date)} {formatTime(schedule.time)}
      {schedule.callOpen || schedule.callClose ? (
        <span className="ml-1 text-slate-400">
          · Cámara de llamadas {formatTime(schedule.callOpen)}
          {schedule.callClose ? `–${formatTime(schedule.callClose)}` : ""}
        </span>
      ) : null}
    </span>
  );
};

const RoundEditor = ({
  prueba,
  round,
  readOnly,
  readOnlyReason,
  canRemove,
  saveRound,
  onDirtyChange,
  onConflict,
  onLocked,
  rowActions,
  showWaPoints,
  unassigned,
  onAddEntry,
  onSearchAtLane,
  headerActions = null,
  notice = null,
}) => {
  const format = prueba?.format;
  const windMode = prueba?.windMode;
  const attemptSlots = resolveAttemptSlots(prueba);
  const attemptsPerHeight = resolveAttemptsPerHeight(prueba);
  const [baseline, setBaseline] = useState(round);
  const [draft, setDraft] = useState(() => buildRoundDraft(round, { attemptSlots, format, attemptsPerHeight }));
  const [saving, setSaving] = useState(false);
  const [serverInvalid, setServerInvalid] = useState(null);
  const nav = useGridNavigation();
  const { confirm, dialog } = useConfirmDialog();

  const payload = useMemo(
    () =>
      buildRoundSave({
        baselineRound: baseline,
        draft,
        format,
        windMode,
        expectedRevision: prueba?.revision,
        attemptSlots,
        attemptsPerHeight,
      }),
    [attemptSlots, attemptsPerHeight, baseline, draft, format, prueba?.revision, windMode]
  );
  const dirty = hasRoundSaveChanges(payload);
  const changedIds = useMemo(() => changedRowIds(payload), [payload]);
  const invalidCells = useMemo(
    () => collectInvalidCells({ draft, format, windMode }),
    [draft, format, windMode]
  );
  const localInvalidKeys = useMemo(() => invalidCellKeys(invalidCells), [invalidCells]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(
    () => () => {
      if (onDirtyChange) onDirtyChange(false);
    },
    [onDirtyChange]
  );

  // Una serie nueva del servidor se adopta solo si no hay cambios locales: nunca se pisa lo que el
  // operador está escribiendo. Con cambios, el borrador sigue comparándose contra su serie de partida.
  useEffect(() => {
    if (round !== baseline && !dirty) {
      setBaseline(round);
      setDraft(buildRoundDraft(round, { attemptSlots, format, attemptsPerHeight }));
    }
  }, [round, baseline, dirty, attemptSlots, attemptsPerHeight, format]);

  const setRow = useCallback((rowId, patchOrFn) => {
    setDraft((current) => {
      const row = current.rows[rowId];
      if (!row) return current;
      const patch = typeof patchOrFn === "function" ? patchOrFn(row) : patchOrFn;
      return { ...current, rows: { ...current.rows, [rowId]: { ...row, ...patch } } };
    });
    setServerInvalid((current) => (current && current.rowId === rowId ? null : current));
  }, []);

  const save = useCallback(async () => {
    if (readOnly || saving) return;
    if (invalidCells.length) {
      toast.error("Corrige las celdas marcadas en rojo antes de guardar.");
      return;
    }
    if (!dirty) {
      toast("No hay cambios por guardar.");
      return;
    }
    setSaving(true);
    try {
      const detail = await saveRound({ pruebaId: prueba.id, roundId: round.id, payload });
      const fresh = (detail?.rounds || []).find((item) => Number(item.id) === Number(round.id));
      if (fresh) {
        setBaseline(fresh);
        setDraft(
          buildRoundDraft(fresh, {
            attemptSlots,
            format,
            attemptsPerHeight: resolveAttemptsPerHeight(detail?.prueba || prueba),
          })
        );
      }
      setServerInvalid(null);
      toast.success(`${round.label || "Serie"} guardada.`);
    } catch (error) {
      const parsed = parseAthleticsError(error);
      if (parsed.isConflict) {
        onConflict(parsed);
        return;
      }
      if (parsed.code === "INVALID_MARK") {
        setServerInvalid(resolveInvalidMarkTarget(parsed.details, baseline?.entries));
      } else if (parsed.code === "INVALID_WIND") {
        setServerInvalid(
          parsed.details?.entryId
            ? resolveInvalidMarkTarget(
                { ...parsed.details, field: parsed.details.field || "wind" },
                baseline?.entries
              )
            : { rowId: null, field: "wind", value: null }
        );
      }
      toast.error(parsed.message);
      if (parsed.isLocked && onLocked) onLocked();
    } finally {
      setSaving(false);
    }
  }, [
    attemptSlots,
    baseline,
    dirty,
    prueba,
    format,
    invalidCells.length,
    onConflict,
    onLocked,
    payload,
    readOnly,
    round?.id,
    round?.label,
    saveRound,
    saving,
  ]);

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === "s") {
        event.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const discard = async () => {
    if (!(await confirm(DISCARD_CHANGES_OPTIONS))) return;
    setBaseline(round);
    setDraft(buildRoundDraft(round, { attemptSlots, format, attemptsPerHeight }));
    setServerInvalid(null);
  };

  const Grid = GRID_BY_FORMAT[format] || null;
  const entries = Array.isArray(baseline?.entries) ? baseline.entries : [];
  // Serie vacía (p. ej. recién agregada) de pista con carriles: se muestran sus carriles libres para llenarla.
  const fillableEmpty =
    !readOnly &&
    (format === "PST" || prueba?.laneMode === "lane") &&
    (typeof onAddEntry === "function" || typeof onSearchAtLane === "function");

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex flex-wrap items-end gap-4">
          {headerActions ? <span className="-mb-0.5 inline-flex items-center gap-0.5 self-center">{headerActions}</span> : null}
          <RoundSchedule schedule={round?.schedule} />
          {windMode === "round" ? (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Viento (m/s)</span>
              <CellInput
                value={draft.wind}
                onChange={(raw) => {
                  setDraft((current) => ({ ...current, wind: sanitizeWindTyping(raw) }));
                  setServerInvalid((current) => (current && current.rowId === null ? null : current));
                }}
                onBlur={() => setDraft((current) => ({ ...current, wind: normalizeWind(current.wind) ?? "" }))}
                disabled={readOnly}
                invalid={isServerInvalidCell(serverInvalid, { rowId: null, kind: "wind" })}
                softInvalid={localInvalidKeys.has("round|wind")}
                placeholder="+0.0"
                title="Viento de la serie: +1.2, -0.5, 0.0"
                ariaLabel="Viento de la serie"
                className="w-20"
              />
            </label>
          ) : null}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Estado de la serie</span>
            <select
              value={draft.status || "pending"}
              onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
              disabled={readOnly}
              className={compactSelectClass}
            >
              {ROUND_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {readOnly ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500">
            <FiLock aria-hidden="true" /> {readOnlyReason || "Solo lectura"}
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {dirty ? (
              <Badge tone="amber" title="Cambios sin guardar">
                Sin guardar
                {changedIds.size ? ` · ${changedIds.size} ${changedIds.size === 1 ? "fila" : "filas"}` : ""}
              </Badge>
            ) : null}
            {dirty ? (
              <button type="button" onClick={discard} disabled={saving} className={secondaryButtonClass}>
                <FiRotateCcw aria-hidden="true" /> Descartar
              </button>
            ) : null}
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className={primaryButtonClass}
              title="Guardar (Ctrl+S)"
            >
              {saving ? <Spinner /> : <FiSave aria-hidden="true" />}
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        )}
      </div>

      {notice ? (
        <p className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-[11px] font-semibold text-slate-500">
          <FiLock aria-hidden="true" /> {notice}
        </p>
      ) : null}

      {!Grid ? (
        <EmptyState title="Formato no soportado" description={`El formato ${format || "desconocido"} no tiene grilla de captura.`} />
      ) : !entries.length && !fillableEmpty ? (
        <EmptyState title="Serie sin inscritos" description="Agrega inscritos a esta serie para capturar resultados." />
      ) : (
        <fieldset disabled={saving} className="min-w-0">
          <Grid
            prueba={prueba}
            round={baseline}
            draft={draft}
            setRow={setRow}
            setDraft={setDraft}
            readOnly={readOnly}
            localInvalidKeys={localInvalidKeys}
            serverInvalid={serverInvalid}
            changedIds={changedIds}
            canRemove={canRemove}
            rowActions={rowActions}
            showWaPoints={showWaPoints}
            attemptSlots={attemptSlots}
            nav={nav}
            unassigned={unassigned}
            onAddEntry={onAddEntry}
            onSearchAtLane={onSearchAtLane}
            poolBlocked={dirty}
          />
        </fieldset>
      )}

      {!readOnly && entries.length ? (
        <p className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
          Enter o ↓ pasa a la fila siguiente · ↑ a la anterior · Ctrl+S guarda la serie.
        </p>
      ) : null}
      {dialog}
    </div>
  );
};

export default RoundEditor;
